import {
  EmailHistoryAggStatusEnum,
  EmailStageStateEnum,
  EntityTypeEnum,
  NotificationChannelsEnum,
  RelatedEntityDTO,
  SupplierNotificationTypeEnum,
} from '@api/ringil-api.v2';
import type useNavigation from '@hooks/useNavigation';

/** Defaults live here; every visible string resolves through a Locize key. */

type Translate = (key: string, defaultValue?: string) => string;

export type EmailStageKey = 'queued' | 'sent' | 'delivered' | 'opened';

export const EMAIL_STAGES: EmailStageKey[] = ['queued', 'sent', 'delivered', 'opened'];

// Wording depends on stage AND state: "Delivering" in flight, "Delivered" once done.
const STAGE_LABEL: Record<EmailStageKey, Record<EmailStageStateEnum, string>> = {
  queued: {
    [EmailStageStateEnum.WAITING]: 'Handing over for sending',
    [EmailStageStateEnum.DONE]: 'Handed over for sending',
    [EmailStageStateEnum.ERROR]: 'Handover failed',
  },
  sent: {
    [EmailStageStateEnum.WAITING]: 'Sending',
    [EmailStageStateEnum.DONE]: 'Sent',
    [EmailStageStateEnum.ERROR]: 'Sending failed',
  },
  delivered: {
    [EmailStageStateEnum.WAITING]: 'Delivering',
    [EmailStageStateEnum.DONE]: 'Delivered',
    [EmailStageStateEnum.ERROR]: 'Delivery failed',
  },
  opened: {
    [EmailStageStateEnum.WAITING]: 'Waiting to be read',
    [EmailStageStateEnum.DONE]: 'Read',
    [EmailStageStateEnum.ERROR]: 'Read failed',
  },
};

export const stageLabel = (tr: Translate, stage: EmailStageKey, state: EmailStageStateEnum): string =>
  tr(`EmailHistory.stage.${stage}.${state}`, STAGE_LABEL[stage][state]);

const CHANNEL_LABEL: Record<NotificationChannelsEnum, string> = {
  [NotificationChannelsEnum.EMAIL]: 'E-mail',
  [NotificationChannelsEnum.SMS]: 'SMS',
  [NotificationChannelsEnum.NOTIFICATION_CENTER]: 'Notification center',
};

/** Keyed by the enum, not by this page, so any surface showing a channel reuses these. */
export const channelLabel = (tr: Translate, channel: NotificationChannelsEnum): string =>
  tr(`NotificationChannels.${channel}`, CHANNEL_LABEL[channel]);

const AGG_STATUS_LABEL: Record<EmailHistoryAggStatusEnum, string> = {
  [EmailHistoryAggStatusEnum.IN_PROGRESS]: 'In progress',
  [EmailHistoryAggStatusEnum.DONE]: 'Delivered',
  [EmailHistoryAggStatusEnum.ERROR]: 'Error',
};

export const aggStatusLabel = (tr: Translate, status: EmailHistoryAggStatusEnum): string =>
  tr(`EmailHistory.aggStatus.${status}`, AGG_STATUS_LABEL[status]);

/** Hand-picked: the backend offers no facet, and EntityTypeEnum carries values no email is sent about. */
export const EMAIL_HISTORY_ENTITY_TYPES: EntityTypeEnum[] = [
  EntityTypeEnum.EXPEDITION,
  EntityTypeEnum.TIMESLOT,
  EntityTypeEnum.SUPPLIER_NOTIFICATION,
  EntityTypeEnum.SUPPLIER_MANIFEST,
  EntityTypeEnum.SHIPMENT,
  EntityTypeEnum.GROUP_DEMAND,
];

const ENTITY_TYPE_LABEL: Partial<Record<EntityTypeEnum, string>> = {
  [EntityTypeEnum.EXPEDITION]: 'Expedition',
  [EntityTypeEnum.TIMESLOT]: 'Reservation',
  [EntityTypeEnum.SUPPLIER_NOTIFICATION]: 'Supplier notification',
  [EntityTypeEnum.SUPPLIER_MANIFEST]: 'Supplier manifest',
  [EntityTypeEnum.SHIPMENT]: 'Shipment',
  [EntityTypeEnum.GROUP_DEMAND]: 'Group demand',
};

/** Reuses the notification centre's existing keys rather than minting a parallel set. */
export const entityTypeLabel = (tr: Translate, entityType: EntityTypeEnum): string =>
  tr(`NotificationCenter.entityType.${entityType}`, ENTITY_TYPE_LABEL[entityType] ?? entityType);

/**
 * Route for a related entity, as a `urlFunctions` descriptor so the caller can run it through
 * `createNavigationLink` and keep the session's `runAs`. Unknown types render as plain text.
 */
export const entityUrlInfo = (urlFunctions: ReturnType<typeof useNavigation>['urlFunctions'], entity: RelatedEntityDTO) => {
  switch (entity.entityType) {
    case EntityTypeEnum.EXPEDITION:
      return urlFunctions.createExpeditionDetailV2(entity.entityId);
    case EntityTypeEnum.TIMESLOT:
      return urlFunctions.createReservationDetail(entity.entityId);
    case EntityTypeEnum.SUPPLIER_MANIFEST:
      return urlFunctions.createSupplierManifestDetail(entity.entityId);
    case EntityTypeEnum.GROUP_DEMAND:
      return urlFunctions.createCustomerGroupDemandDetail(entity.entityId);
    case EntityTypeEnum.VISITOR_RECORD:
      return urlFunctions.createVisitDetail(entity.entityId);
    // SnTypeEnum and SupplierNotificationTypeEnum carry the same four values; only EFXX and DXX
    // occur in email data today, so an unset type falls through to a plain tag.
    case EntityTypeEnum.SUPPLIER_NOTIFICATION:
      return entity.snType
        ? urlFunctions.createSupplierNotificationDetail(entity.entityId, entity.snType as unknown as SupplierNotificationTypeEnum)
        : undefined;
    // Keyed by application id, not by the entity's uuid, unlike every other detail route.
    case EntityTypeEnum.SHIPMENT:
      return entity.applicationId ? urlFunctions.createShipmentDetail(entity.applicationId) : undefined;
    default:
      return undefined;
  }
};
